import { isLeft } from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import type { Decoder, TypeOf } from 'io-ts/Decoder';
import {
	array,
	boolean,
	failure,
	intersect,
	literal,
	nullable,
	number,
	partial,
	parse,
	string,
	struct,
	success,
	tuple,
	union,
} from 'io-ts/Decoder';

import { iotsUtils } from '@bedc/core';

import { Id as BuilderId } from '../../../../builder/Id';
import { Id as ClientId } from '../../../../client/Id';
import { CountryCode } from '../../../../country/CountryCode';
import { Id as CustomerId } from '../../../../customer/Id';
import { Id as AllocationTypeId } from '../../../../job/allocation-type';
import { Id as JobTypeId } from '../../../../job/job-type';
import { Id as RequestTypeId } from '../../../../job/request-type';
import { Id as RequirementId } from '../../../../job/requirement';
// import { Id as JobStageId } from '../../../../job/stage';
// import { Id as JobStatusId } from '../../../../job/status';
import { Id as JobStatusCategoryId } from '../../../../job/status/category';
import { Id as OrganizationId } from '../../../../organization/Id';
import { Id as ProjectId } from '../../../../project/Id';
import { Timezone } from '../../../../time/timezone/Timezone';
import { Id as UserId } from '../../../../user/Id';

import { EqualsType } from './EqualsType';

export const Request = <
	StandardFilterField extends string,
	DateFilterField extends string,
	StageStatusFilterField extends string,
	BooleanFilterField extends string,
>({
	standardFilterFields,
	dateFilterFields,
	stageStatusFilterFields,
	booleanFilterFields,
}: {
	standardFilterFields: readonly StandardFilterField[];
	dateFilterFields: readonly DateFilterField[];
	stageStatusFilterFields: readonly StageStatusFilterField[];
	booleanFilterFields: readonly BooleanFilterField[];
}) => {
	// type TypeFromUnion<A extends string, B> = {
	// 	[K in A]: {
	// 		type: K;
	// 		values: string[];
	// 	};
	// }[A];

	const standardFilterDecoder = pipe(
		struct({
			field: iotsUtils.fromList(standardFilterFields),
			equalsType: EqualsType,
			values: array(string),
		}),
		parse((param) => {
			switch (param.field) {
				case 'builderId': {
					const decoded = array(BuilderId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'clientId': {
					const decoded = array(ClientId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'countryCode': {
					const decoded = array(CountryCode).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'customerId': {
					const decoded = array(CustomerId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'organizationId': {
					const decoded = array(OrganizationId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'projectId': {
					const decoded = array(ProjectId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'userId':
				case 'createdBy':
				case 'owner':
				case 'assignedTo': {
					const decoded = array(UserId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'statusCategory': {
					const decoded = array(JobStatusCategoryId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				// ! Disabled as saved dashboards have invalid stages (e.g. 'estimate') ROME-7218
				// case 'status': {
				// 	const stageIds = param.values.map(
				// 		(value) => value.split(':')[0]
				// 	);
				// 	const statusIds = param.values.map(
				// 		(value) => value.split(':')[1]
				// 	);

				// 	const decodedStage = array(JobStageId).decode(stageIds);
				// 	const decodedStatus =
				// 		array(JobStatusId).decode(statusIds);

				// 	if (isLeft(decodedStage)) {
				// 		return failure(stageIds, `valid ${param.field}`);
				// 	}
				// 	if (isLeft(decodedStatus)) {
				// 		return failure(statusIds, `valid ${param.field}`);
				// 	}

				// 	break;
				// }

				case 'allocationType': {
					const decoded = array(AllocationTypeId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'jobType': {
					const decoded = array(JobTypeId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'requestType': {
					const decoded = array(RequestTypeId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}

				case 'requirements': {
					const decoded = array(RequirementId).decode(param.values);

					if (isLeft(decoded)) {
						return failure(param.values, `valid ${param.field}`);
					}

					break;
				}
			}

			return success(param);
		})
	) as Decoder<
		unknown,
		{
			[K in StandardFilterField]: {
				field: K;
				equalsType: TypeOf<typeof EqualsType>;
				values: string[];
			};
		}[StandardFilterField]
	>;

	const dateFilterDecoder = union(
		intersect(
			struct({
				field: iotsUtils.fromList(dateFilterFields),
				rangeType: literal('between'),
				values: tuple(nullable(union(string, number)), nullable(union(string, number))),
			})
		)(partial({ timezone: Timezone })),
		struct({
			field: iotsUtils.fromList(dateFilterFields),
			rangeType: union(literal('last'), literal('next')),
			amount: iotsUtils.PositiveInteger1Based,
			unit: union(literal('day'), literal('week'), literal('month')),
			timezone: Timezone,
		}),
		struct({
			field: iotsUtils.fromList(dateFilterFields),
			rangeType: union(literal('current'), literal('previous')),
			unit: union(literal('day'), literal('month'), literal('year')),
			timezone: Timezone,
		})
	) as Decoder<
		unknown,
		{
			[K in DateFilterField]:
				| {
						field: K;
						rangeType: 'between';
						values: [string | number | null, string | number | null];
						timezone?: TypeOf<typeof Timezone>;
				  }
				| {
						field: K;
						rangeType: 'last' | 'next';
						amount: number;
						unit: 'day' | 'week' | 'month';
						timezone: TypeOf<typeof Timezone>;
				  }
				| {
						field: K;
						rangeType: 'current' | 'previous';
						unit: 'day' | 'month' | 'year';
						timezone: TypeOf<typeof Timezone>;
				  };
		}[DateFilterField]
	>;

	const stageStatusFilterDecoder = struct({
		field: iotsUtils.fromList(stageStatusFilterFields),
		equalsType: EqualsType,
		stageId: string,
		// aka statusIds
		values: array(string),
	}) as Decoder<
		unknown,
		{
			[K in StageStatusFilterField]: {
				field: K;
				equalsType: TypeOf<typeof EqualsType>;
				stageId: string;
				values: string[];
			};
		}[StageStatusFilterField]
	>;

	const booleanFilterDecoder = struct({
		field: iotsUtils.fromList(booleanFilterFields),
		equalsType: EqualsType,
		value: boolean,
	}) as Decoder<
		unknown,
		{
			[K in BooleanFilterField]: {
				field: K;
				equalsType: TypeOf<typeof EqualsType>;
				value: boolean;
			};
		}[BooleanFilterField]
	>;

	return array(
		union(
			standardFilterDecoder,
			dateFilterDecoder,
			stageStatusFilterDecoder,
			booleanFilterDecoder
		)
	);
};
